import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// Initialize Google Analytics
const initGA = (measurementId: string) => {
  if (window.gtag) return; // Already initialized

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We'll handle page views manually
  });

  // Load the GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
};

// Track page views
export const trackGAPageView = (pagePath: string, pageTitle: string) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

// Track custom events
export const trackGAEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// Track view content (product views)
export const trackGAViewContent = (
  itemId: string,
  itemName: string,
  price: number,
  currency = 'USD'
) => {
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency,
      value: price,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          price,
        },
      ],
    });
  }
};

// Track add to cart
export const trackGAAddToCart = (
  itemId: string,
  itemName: string,
  price: number,
  currency = 'USD'
) => {
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency,
      value: price,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          price,
          quantity: 1,
        },
      ],
    });
  }
};

// Track begin checkout
export const trackGABeginCheckout = (
  value: number,
  currency: string,
  items: Array<{ id: string; name: string; price: number; quantity: number }>
) => {
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency,
      value,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
    });
  }
};

// Track purchase
export const trackGAPurchase = (
  transactionId: string,
  value: number,
  currency: string,
  items: Array<{ id: string; name: string; price: number; quantity: number }>
) => {
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      currency,
      value,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
    });
  }
};

// Track lead/signup
export const trackGALead = (value?: number, currency = 'USD') => {
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      currency,
      value: value || 0,
    });
  }
};

const GoogleAnalytics = () => {
  const location = useLocation();
  const initialized = useRef(false);

  // Fetch active GA pixel from database
  const { data: gaPixel } = useQuery({
    queryKey: ['tracking-pixel-ga'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tracking_pixels')
        .select('tracking_id')
        .eq('type', 'google_analytics')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Failed to fetch GA pixel:', error);
        return null;
      }
      return data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Initialize GA when pixel is loaded
  useEffect(() => {
    if (gaPixel?.tracking_id && !initialized.current) {
      initialized.current = true;

      // Defer initialization for better performance
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
          initGA(gaPixel.tracking_id);
          trackGAPageView(location.pathname, document.title);
        });
      } else {
        setTimeout(() => {
          initGA(gaPixel.tracking_id);
          trackGAPageView(location.pathname, document.title);
        }, 2000);
      }
    }
  }, [gaPixel?.tracking_id, location.pathname]);

  // Track page views on route change
  useEffect(() => {
    if (window.gtag && initialized.current) {
      trackGAPageView(location.pathname, document.title);
    }
  }, [location.pathname]);

  return null;
};

export default GoogleAnalytics;
